import type {
  FilterCondition,
  FilterFieldMeta,
  FilterGroup,
  FilterNode,
  FilterOperator,
  SortSpec,
} from '@aurora/contracts';
import { CURRENT_ACTOR_GROUPS_TOKEN, FILTER_OPERATORS, isDateExpression, isFilterGroup } from '@aurora/contracts';

/**
 * The ticket list's filter logic, kept free of React so it can be tested on
 * its own.
 *
 * Three sources make up what the list asks the server for — a saved view, the
 * quick chips, and the advanced builder — and they always *narrow* together.
 * They used to be merged into one flat list that took on whatever top-level
 * logic the builder had, so a builder set to "or" turned every chip into one
 * more way to widen the results: "Urgent or Breached", plus "My tickets", came
 * back as every urgent ticket, every breached one and everything of mine.
 */

export const EMPTY_GROUP: FilterGroup = { logic: 'and', nodes: [] };

/** Operators that take no value. */
export const VALUELESS: readonly FilterOperator[] = ['is_empty', 'is_not_empty'];

/** Operators whose value is a list of choices. `between` is a pair, handled apart. */
export const LIST_OPERATORS: readonly FilterOperator[] = ['in', 'not_in', 'has_any', 'has_all', 'has_none'];

// ---------------------------------------------------------------------------
// Reading what was stored
// ---------------------------------------------------------------------------

/**
 * A filter as a saved view or a URL holds it, made safe to send.
 *
 * A view created without a filter is stored as `{}`, which is not a valid
 * filter — sending it back would be a 400 — so anything that is not a real
 * condition or a non-empty group reads as "no filter".
 */
export function asFilterNode(raw: unknown): FilterNode | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;

  if ('logic' in record || 'nodes' in record) {
    if (!Array.isArray(record.nodes)) return null;
    const nodes = record.nodes.map(asFilterNode).filter((node): node is FilterNode => node !== null);
    if (nodes.length === 0) return null;
    return { logic: record.logic === 'or' ? 'or' : 'and', nodes };
  }

  if (
    typeof record.field === 'string' &&
    record.field.length > 0 &&
    typeof record.op === 'string' &&
    (FILTER_OPERATORS as readonly string[]).includes(record.op)
  ) {
    return {
      field: record.field,
      op: record.op as FilterOperator,
      ...('value' in record ? { value: record.value } : {}),
    };
  }
  return null;
}

/** The builder's filter from the `filter` URL parameter. A broken link reads as "no filter". */
export function parseFilterParam(raw: string | null): FilterGroup {
  if (!raw) return EMPTY_GROUP;
  try {
    const node = asFilterNode(JSON.parse(raw));
    if (!node) return EMPTY_GROUP;
    return isFilterGroup(node) ? node : { logic: 'and', nodes: [node] };
  } catch {
    return EMPTY_GROUP;
  }
}

/** The builder's filter as the URL holds it, or null for none. */
export function serialiseFilter(group: FilterGroup): string | null {
  const clean = withoutEmptyGroups(group);
  return clean.nodes.length > 0 ? JSON.stringify(clean) : null;
}

/**
 * `-priority,created_at` → priority descending, then created ascending.
 * `sla` is the urgency order (see `SLA_URGENCY_SORT`), by name.
 */
export function parseSortParam(raw: string | null): SortSpec[] {
  if (!raw) return [];
  if (raw.trim() === URGENCY_TOKEN) return SLA_URGENCY_SORT.map((spec) => ({ ...spec }));
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part !== '-')
    .slice(0, 4)
    .map((part) =>
      part.startsWith('-')
        ? { field: part.slice(1), direction: 'desc' as const }
        : { field: part, direction: 'asc' as const },
    );
}

export function serialiseSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null;
  if (sameSort(sort, SLA_URGENCY_SORT)) return URGENCY_TOKEN;
  return sort.map((spec) => (spec.direction === 'desc' ? `-${spec.field}` : spec.field)).join(',');
}

/** Sort as a saved view stores it: an array of specs, or anything else meaning none. */
export function asSortSpecs(raw: unknown): SortSpec[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (entry): entry is { field: string; direction?: string; nulls?: string } =>
        !!entry && typeof entry === 'object' && typeof (entry as { field?: unknown }).field === 'string',
    )
    .slice(0, 4)
    .map((entry) => ({
      field: entry.field,
      direction: entry.direction === 'desc' ? 'desc' : 'asc',
      // Kept: the urgency order needs finished tickets' empty dates first.
      ...(entry.nulls === 'first' || entry.nulls === 'last' ? { nulls: entry.nulls } : {}),
    }));
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** What the server orders a ticket list by when it is not asked for anything. */
export const SERVER_DEFAULT_SORT: SortSpec[] = [{ field: 'last_activity_at', direction: 'desc' }];

/**
 * Most urgent first: open work before finished work, breached before merely
 * due, then the nearest deadline still running.
 *
 * `sla_due_at` alone cannot say it. A breached target stops running, so a
 * ticket whose only target has breached has no running deadline and sorted
 * after every ticket that was merely due — the most urgent work at the bottom
 * of a list sorted by urgency. A breach is the one thing that marks a ticket
 * escalated, so escalated-first puts it back on top.
 *
 * Escalated is never cleared, though, so on a list that also holds resolved
 * tickets every one that ever breached came first, burying the open breaches
 * among months of finished work. Finished tickets are the ones with a
 * resolution date, so empty-dates-first puts every open ticket ahead of them.
 */
export const SLA_URGENCY_SORT: SortSpec[] = [
  { field: 'resolved_at', direction: 'desc', nulls: 'first' },
  { field: 'is_escalated', direction: 'desc' },
  { field: 'sla_due_at', direction: 'asc' },
];

/** The urgency order's name in a link — `sort=sla` rather than its three parts. */
const URGENCY_TOKEN = 'sla';

function specKey(spec: SortSpec): string {
  // The server puts empty values last unless told otherwise.
  return `${spec.direction === 'desc' ? '-' : ''}${spec.field}~${spec.nulls ?? 'last'}`;
}

export function sameSort(a: SortSpec[], b: SortSpec[]): boolean {
  return a.length === b.length && a.every((spec, index) => specKey(spec) === specKey(b[index]!));
}

/** "Last activity, newest first" — so the order a list is in is never a mystery. */
export function describeSort(sort: SortSpec[], fields: Map<string, FilterFieldMeta>): string {
  const effective = sort.length > 0 ? sort : SERVER_DEFAULT_SORT;
  if (sameSort(effective, SLA_URGENCY_SORT)) {
    return 'Most urgent first: breached, then the nearest SLA deadline, with resolved and closed tickets last';
  }
  return effective.map((spec) => describeSortSpec(spec, fields.get(spec.field))).join(', then ');
}

function describeSortSpec(spec: SortSpec, meta: FilterFieldMeta | undefined): string {
  const label = meta?.label ?? spec.field.replace(/_/g, ' ');
  const desc = spec.direction === 'desc';
  const type = meta?.type ?? (/_at$/.test(spec.field) ? 'datetime' : 'string');
  let order: string;
  if (type === 'date' || type === 'datetime') {
    // A deadline is "soonest", a record of something that happened is "newest".
    order = /due/.test(spec.field) ? (desc ? 'latest first' : 'soonest first') : desc ? 'newest first' : 'oldest first';
  } else if (type === 'boolean') {
    order = desc ? 'yes first' : 'no first';
  } else if (type === 'string' || type === 'text' || type === 'reference') {
    order = desc ? 'Z to A' : 'A to Z';
  } else {
    order = desc ? 'highest first' : 'lowest first';
  }
  return `${label}, ${order}`;
}

// ---------------------------------------------------------------------------
// Combining
// ---------------------------------------------------------------------------

/**
 * Removes structure that means nothing: a group inside a group with the same
 * logic is the same as its children, and a group of one is that one.
 *
 * This matters beyond tidiness. The server bounds nesting at five levels, and a
 * view saved from a list that already had a view, chips and a builder group
 * would otherwise nest a level deeper every time it was saved and reopened.
 */
export function simplifyFilter(node: FilterNode): FilterNode | null {
  if (!isFilterGroup(node)) return node;
  const children: FilterNode[] = [];
  for (const child of node.nodes) {
    const simple = simplifyFilter(child);
    if (!simple) continue;
    if (isFilterGroup(simple) && simple.logic === node.logic) children.push(...simple.nodes);
    else children.push(simple);
  }
  if (children.length === 0) return null;
  if (children.length === 1) return children[0]!;
  return { logic: node.logic, nodes: children };
}

/**
 * Everything that narrows the list, combined with AND.
 *
 * Each part keeps its own logic: a builder group set to "or" stays an "or"
 * inside the AND, so "(Urgent or Breached) and mine" means exactly that.
 */
export function combineFilters(parts: Array<FilterNode | null | undefined>): FilterNode | null {
  const nodes = parts.filter((part): part is FilterNode => !!part && (!isFilterGroup(part) || part.nodes.length > 0));
  if (nodes.length === 0) return null;
  return simplifyFilter({ logic: 'and', nodes });
}

// ---------------------------------------------------------------------------
// Quick chips
// ---------------------------------------------------------------------------

export type QuickDimension = 'assignment' | 'status' | 'sla' | 'priority' | 'created';

export interface QuickFilter {
  id: string;
  label: string;
  dimension: QuickDimension;
  /** One condition. Chips in the same dimension are combined with OR. */
  condition: FilterCondition;
}

export const OPEN_CATEGORIES: FilterCondition = {
  field: 'status_category',
  op: 'in',
  value: ['open', 'pending'],
};

/**
 * `@me` and `@my_groups` are resolved by the server from the session, never
 * from a value the client supplies — which is what lets a shared link mean
 * "mine" for whoever opens it without becoming a way to read somebody else's
 * queue.
 */
export const QUICK_FILTERS: QuickFilter[] = [
  { id: 'mine', label: 'Mine', dimension: 'assignment', condition: { field: 'assignee_id', op: 'is', value: '@me' } },
  { id: 'unassigned', label: 'Unassigned', dimension: 'assignment', condition: { field: 'assignee_id', op: 'is_empty' } },
  {
    id: 'my_groups',
    label: 'My groups',
    dimension: 'assignment',
    condition: { field: 'group_id', op: 'in', value: CURRENT_ACTOR_GROUPS_TOKEN },
  },
  { id: 'open', label: 'Open', dimension: 'status', condition: OPEN_CATEGORIES },
  { id: 'breached', label: 'Breached', dimension: 'sla', condition: { field: 'sla_state', op: 'in', value: ['breached'] } },
  { id: 'at_risk', label: 'At risk', dimension: 'sla', condition: { field: 'sla_state', op: 'in', value: ['at_risk'] } },
  {
    id: 'urgent',
    label: 'High & urgent',
    dimension: 'priority',
    condition: { field: 'priority', op: 'in', value: ['high', 'urgent'] },
  },
  {
    id: 'today',
    label: 'Created today',
    dimension: 'created',
    condition: { field: 'created_at', op: 'is', value: { relative: 'today' } },
  },
];

export const QUICK_DIMENSION_LABELS: Record<QuickDimension, string> = {
  assignment: 'Assignment',
  status: 'Status',
  sla: 'SLA',
  priority: 'Priority',
  created: 'Created',
};

/**
 * The chips somebody can use. A restricted agent sees only what is assigned to
 * them, so "Unassigned" and "My groups" could only ever come back empty.
 */
export function quickFiltersFor(options: { assignedOnly: boolean }): QuickFilter[] {
  if (!options.assignedOnly) return QUICK_FILTERS;
  return QUICK_FILTERS.filter((quick) => quick.id !== 'unassigned' && quick.id !== 'my_groups');
}

/**
 * The chips' contribution to the filter: one node per dimension, OR within it.
 *
 * "Breached" and "At risk" together mean either — the question a desk asks
 * most — rather than a ticket somehow in both states at once, which is what
 * ANDing every chip used to ask for, and why it always came back empty.
 *
 * The SLA chips also mean *open* tickets. A breach is never cleared, so a
 * resolved ticket stays "breached" for ever, and the chip on its own listed
 * months of closed work under a heading that reads like a to-do list.
 */
export function quickFilterNodes(ids: ReadonlySet<string>, available: QuickFilter[] = QUICK_FILTERS): FilterNode[] {
  const byDimension = new Map<QuickDimension, FilterCondition[]>();
  for (const quick of available) {
    if (!ids.has(quick.id)) continue;
    byDimension.set(quick.dimension, [...(byDimension.get(quick.dimension) ?? []), quick.condition]);
  }

  const nodes: FilterNode[] = [];
  for (const [dimension, conditions] of byDimension) {
    if (dimension === 'sla') {
      const states = conditions.flatMap((condition) => (Array.isArray(condition.value) ? condition.value : []));
      nodes.push({ field: 'sla_state', op: 'in', value: states });
      // "Open" already says it when that chip is on too.
      if (!byDimension.has('status')) nodes.push(OPEN_CATEGORIES);
      continue;
    }
    nodes.push(conditions.length === 1 ? conditions[0]! : { logic: 'or', nodes: conditions });
  }
  return nodes;
}

/** The ids in a `quick` URL parameter that this list actually offers. */
export function parseQuickParam(raw: string | null, available: QuickFilter[] = QUICK_FILTERS): Set<string> {
  const known = new Set(available.map((quick) => quick.id));
  return new Set((raw ?? '').split(',').map((id) => id.trim()).filter((id) => known.has(id)));
}

// ---------------------------------------------------------------------------
// The requester's list
// ---------------------------------------------------------------------------

export type RequesterShow = 'open' | 'resolved' | 'all';

/** A requester thinks in "still going" and "done", not in status categories. */
export const REQUESTER_SHOW: Record<RequesterShow, { label: string; filter: FilterCondition | null }> = {
  open: { label: 'Open', filter: OPEN_CATEGORIES },
  resolved: { label: 'Resolved', filter: { field: 'status_category', op: 'in', value: ['resolved', 'closed'] } },
  all: { label: 'All', filter: null },
};

export function parseRequesterShow(raw: string | null): RequesterShow {
  return raw === 'resolved' || raw === 'all' ? raw : 'open';
}

// ---------------------------------------------------------------------------
// Completeness
// ---------------------------------------------------------------------------

function isFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (isDateExpression(value)) return true;
  return false;
}

/**
 * Whether a condition says enough to be sent.
 *
 * A half-built condition used to be applied the moment it appeared: "+
 * Condition" added "Ticket ID is ''", which the server read as ticket 0 and
 * emptied the list; picking a date field added "Created is ''", which the
 * server rejected, replacing the whole table with an error. Nothing is sent now
 * until each condition is complete.
 */
export function isConditionComplete(condition: FilterCondition, meta?: FilterFieldMeta): boolean {
  if (!condition.field) return false;
  if (meta && !meta.operators.includes(condition.op)) return false;
  if (VALUELESS.includes(condition.op)) return true;

  const value = condition.value;
  if (condition.op === 'between') {
    return Array.isArray(value) && value.length === 2 && value.every(isFilled);
  }
  if (LIST_OPERATORS.includes(condition.op)) {
    // `@my_groups` is a list the server expands.
    if (value === CURRENT_ACTOR_GROUPS_TOKEN) return true;
    return Array.isArray(value) && value.length > 0 && value.every(isFilled);
  }
  if (meta?.type === 'number' || meta?.type === 'decimal' || meta?.type === 'duration') {
    return isFilled(value) && Number.isFinite(Number(value));
  }
  return isFilled(value);
}

/**
 * Every incomplete condition, by its path of indexes through the groups. A
 * condition on a field the server no longer offers counts: sending it would be
 * refused.
 */
export function incompletePaths(
  group: FilterGroup,
  fields: Map<string, FilterFieldMeta>,
  prefix: number[] = [],
): number[][] {
  const out: number[][] = [];
  group.nodes.forEach((node, index) => {
    const path = [...prefix, index];
    if (isFilterGroup(node)) {
      if (node.nodes.length === 0) out.push(path);
      else out.push(...incompletePaths(node, fields, path));
      return;
    }
    const meta = fields.get(node.field);
    if ((fields.size > 0 && !meta) || !isConditionComplete(node, meta)) out.push(path);
  });
  return out;
}

/** The group with empty sub-groups removed — a group with no conditions is invalid on the wire. */
export function withoutEmptyGroups(group: FilterGroup): FilterGroup {
  const nodes: FilterNode[] = [];
  for (const node of group.nodes) {
    if (isFilterGroup(node)) {
      const inner = withoutEmptyGroups(node);
      if (inner.nodes.length > 0) nodes.push(inner);
    } else {
      nodes.push(node);
    }
  }
  return { logic: group.logic, nodes };
}

export function countFilterConditions(node: FilterNode | null | undefined): number {
  if (!node) return 0;
  return isFilterGroup(node) ? node.nodes.reduce((total, child) => total + countFilterConditions(child), 0) : 1;
}
